import { FormControlLabel, Switch, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../shared/components/FormDialog';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { cargosHooks } from './hooks';
import { cargoInputSchema, type CargoFormValues } from './schemas';
import type { CargoDto } from './types';

export interface CargoFormDialogProps {
  open: boolean;
  /** `null` = Create; objeto = Edit. */
  cargo: CargoDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: CargoFormValues = { descricao: '', ativo: true };

export function CargoFormDialog({ open, cargo, onClose }: CargoFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    CargoFormValues,
    CargoDto,
    CargoFormValues,
    number
  >({
    open,
    entity: cargo,
    schema: cargoInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (c) => ({ descricao: c.descricao, ativo: c.ativo }),
    getId: (c) => c.id,
    hooks: { useCreate: cargosHooks.useCreate, useUpdate: cargosHooks.useUpdate },
    messages: {
      created: t('features.cargos.toasts.created'),
      updated: t('features.cargos.toasts.updated'),
    },
    focusField: 'descricao',
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={isEdit ? t('features.cargos.editTitle') : t('features.cargos.createTitle')}
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
    >
      <TextField
        label={t('features.cargos.fields.descricao')}
        fullWidth
        inputProps={{ maxLength: 150 }}
        error={Boolean(form.formState.errors.descricao)}
        helperText={form.formState.errors.descricao?.message}
        {...form.register('descricao')}
      />
      <Controller
        control={form.control}
        name="ativo"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={field.value ?? true}
                onChange={(_, checked) => field.onChange(checked)}
                onBlur={field.onBlur}
                inputRef={field.ref}
              />
            }
            label={t('features.cargos.fields.ativo')}
          />
        )}
      />
    </FormDialog>
  );
}
